import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import makeStyles from "utils/makeStyles";
import withStyles from "utils/withStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableFooter from "@mui/material/TableFooter";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { Loader } from "components/Common";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import _ from "lodash";
import TablePaginationActions from "components/Common/TableGenerator/TablePaginationActions";
import "./style.css";

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, fields, isDashboardSales } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      style={{ position: "sticky", top: 0, backgroundColor: "#ECF0F1" }}
    >
      <TableRow className={classes.headRow}>
        {!isDashboardSales && <StyledTableCell colSpan={1} />}
        {fields
          .filter((x) => x.visible)
          .map((headCell, __i) => (
            <StyledTableCell
              key={`${headCell.key || headCell.label || "col"}-${__i}`}
              align={headCell.align ? headCell.align : "left"}
              sortDirection={orderBy === headCell.key ? order : false}
            >
              {headCell?.disableColumnSort ? (
                <TableRow className={classes.headCell}>
                  {headCell.columnName}
                </TableRow>
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.key}
                  direction={orderBy === headCell.key ? order : "asc"}
                  onClick={createSortHandler(headCell.key)}
                  className={classes.headCell}
                  style={{ paddingRight: headCell?.paddingRight }}
                >
                  {headCell.columnName}
                  {orderBy === headCell.key ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              )}
            </StyledTableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  fields: PropTypes.array.isRequired,
};

const StyledTableRow = withStyles((theme) => ({
  root: {
    minHeight: 50,
    "&:nth-of-type(even)": {
      backgroundColor: "#fafafc",
    },
  },
}))(TableRow);

const StyledTableCell = withStyles((theme) => ({
  head: {
    fontWeight: "bold",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: 12,
    lineHeight: 1.4,
  },
  body: {
    fontSize: 12,
    color: "#686770",
  },
  root: {
    borderBottom: "none",
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    border: "1px solid #dbdbdb",
    margin: "50!important",
    overflow: "auto",
    position: "relative",
  },
  headRow: {
    background: "#f4f4fb",
  },
  root: {
    width: "100%",
    display: "inline",
  },
  paper: {
    position: "relative",
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    backgroundColor: "#ECF0F1",
  },
  headCell: {
    fontWeight: "bold",
    fontSize: "12px",
    width: "100%",
    textAlign: "center",
  },
  pagination: {
    fontSize: "10px",
  },
  tableRowSelected: {
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "transparent",
    },
  },
  tableCell: {
    fontSize: "12px",
    marginLeft: "100px",
  },
  selected: {},
  ul: {
    "& .MuiPaginationItem-root": {
      color: "#686770",
    },
    "& .Mui-selected": {
      color: "#fff",
      border: "none",
    },
  },
  pagination: {
    marginTop: 15,
    marginBottom: 15,
  },
}));

const Row = ({
  isItemSelected,
  record,
  collapsible,
  sensorTable,
  handleClick,
  labelId,
  fields,
  index,
  defaultPadding,
  isDashboardSales,
}) => {
  const classes = useStyles();

  const [collapseOpen, setCollapseOpen] = useState(false);

  return (
    <>
      <StyledTableRow
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={record.name}
        classes={{ selected: classes.selected }}
        className={classes.tableRowSelected}
        selected={isItemSelected}
        style={{ height: 53 }}
      >
        {!isDashboardSales && (
          <>
            {collapsible ? (
              <StyledTableCell>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setCollapseOpen((prevState) => !prevState)}
                >
                  {collapseOpen ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              </StyledTableCell>
            ) : (
              <StyledTableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  style={{ visibility: sensorTable && "hidden" }}
                  onClick={(event) => handleClick(event, record)}
                  checked={isItemSelected}
                  slotProps={{ input: { "aria-labelledby": labelId } }}
                />
              </StyledTableCell>
            )}
          </>
        )}
        {fields
          .filter((x) => x.visible)
          .map((field) => (
            <StyledTableCell
              key={field.key}
              className={[
                classes.tableCell,
                defaultPadding && classes.defaultPadding,
              ]}
              align={field.align ? field.align : "left"}
            >
              <div style={{ paddingRight: field?.paddingRight }}>
                {field.render && typeof field.render === "function"
                  ? field.render(record[field.key], record, index)
                  : record[field.key]}
              </div>
            </StyledTableCell>
          ))}
      </StyledTableRow>
      {collapsible && collapseOpen && (
        <TableRow>
          <StyledTableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={fields.length}
          >
            {_.map(record.answer_sheet, (x, index) => (
              <React.Fragment key={index}>
                <div
                  style={{
                    marginLeft: "80px", // Shifts content to the right
                    marginTop: "12px", // Adds space above each new set (except the first one)
                    paddingBottom: "12px", // Adds space at the bottom inside the div
                    borderBottom: "1px solid #e0e0e0", // Optional: adds a line separator between sets

                    color: "#686770",
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  <div>
                    <span style={{ fontWeight: "bold" }}>
                      Question {index + 1} :
                    </span>{" "}
                    {x.question}
                  </div>
                  <br />
                  <div>
                    <span style={{ fontWeight: "bold" }}>Answer :</span>{" "}
                    {x.answer}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
};

const TableGenerator = ({ handleSortChange = () => {}, showSelectAll = true, selectedRecords = [],
  fields = [],
  data = [],
  currentPage = 0,
  initialSort = "",
  backendPagination = false,
  dataCount,
  searchQuery = "",
  onPageChange = () => {},
  onChangeSelected = () => {},
  onRowPerPageChange = () => {},
  rowOnePage = 10,
  radio = false,
  size = "medium",
  sensorTable = false,
  collapsible = false,
  collapsibleFieldKey,
  collapsibleJson = [],
  collapsibleFields,
  collapsibleHeader,
  collapsibleFieldKey2,
  collapsibleFields2,
  collapsibleHeader2,
  collapsibleFieldKey3,
  collapsibleHeader3,
  collapsibleFields3,
  isDashboardSales = false,
  sortOrder = "desc",
  ...props
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState(sortOrder);
  const [orderBy, setOrderBy] = useState(initialSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowOnePage);
  const [collapseOpen, setCollapseOpen] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    handleSortChange(`${isAsc ? "" : "-"}${property}`);
  };

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleClick = (event, name) => {
    const selectedIndex = selectedRecords.findIndex(
      (val) => val.id === name.id
    );
    let newSelected = [];

    if (radio) {
      if (selectedIndex) {
        newSelected = [name];
      } else {
        newSelected = [];
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRecords, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRecords.slice(1));
      } else if (selectedIndex === selectedRecords.length - 1) {
        newSelected = newSelected.concat(selectedRecords.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRecords.slice(0, selectedIndex),
          selectedRecords.slice(selectedIndex + 1)
        );
      }
    }
    onChangeSelected(newSelected);
  };

  const handleChangePage = (event, newPage, towards) => {
    setPage(newPage);
    onPageChange(newPage, towards);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onRowPerPageChange(event.target.value);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      onChangeSelected(data);
    } else {
      onChangeSelected([]);
    }
  };

  const isSelected = (record) =>
    selectedRecords.findIndex((x) => x.id === record.id) !== -1;

  return (
    <Paper
      elevation={0}
      className={`${classes.tableContainer} ${
        props.stickyTable ? "sticky-table" : ""
      }`}
    >
      <Loader visible={props.loader} />
      <Table
        className={!isDashboardSales ?? classes.table}
        aria-label="customized table"
        style={{ height: props.tableHeight ?? props.tableHeight }}
      >
        <EnhancedTableHead
          classes={classes}
          numSelected={selectedRecords.length}
          order={order}
          collapsible={collapsible}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          rowCount={data.length}
          fields={fields}
          data={data}
          showSelectAll={props.showSelectAll}
          selectedRecords={selectedRecords}
          onSelectAll={handleSelectAll}
          isDashboardSales={isDashboardSales}
        />
        <TableBody>
          {data
            .slice(
              backendPagination ? 0 : page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            )
            .map((record, index) => {
              const isItemSelected = isSelected(record);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <Row
                  labelId={labelId}
                  collapsibleFields={collapsibleFields}
                  fields={fields}
                  sensorTable={sensorTable}
                  handleClick={handleClick}
                  collapsibleFieldKey={collapsibleFieldKey}
                  index={index}
                  collapsibleHeader={collapsibleHeader}
                  isItemSelected={isItemSelected}
                  record={record}
                  collapsible={collapsible}
                />
              );
            })}
          {!props.loader && data.length === 0 && (
            <TableRow>
              <StyledTableCell
                colSpan={100}
                align="center"
                style={{ border: 0, padding: "24px" }}
              >
                <strong> No records found</strong>
              </StyledTableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter className={`${props.stickyTable ? "sticky-footer" : ""}`}>
          <StyledTableRow
            style={{
              border: "none",
              borderTop: "0.5px solid #dbdbdb",
              width: "100%",
            }}
          >
            <TablePagination
              style={{ border: "none" }}
              rowsPerPageOptions={[3, 5, 7, 8, 10, 25, 50, 100]}
              colSpan={fields.length}
              count={dataCount !== undefined ? dataCount : data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{ select: { "aria-label": "rows per page" } }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={(e) => TablePaginationActions(e, props)}
            />
          </StyledTableRow>
        </TableFooter>
      </Table>
    </Paper>
  );
};


export default withStyles({}, { withTheme: true })(TableGenerator);
